import { Alert, getSymbol, Input, Label, Modal, Select, SubmitButton, Table } from "@xola/ui-kit";
import { DemographicIcon, FormField, InputField, isEquipmentBased } from "common";
import { isNil, sortBy } from "lodash";
import React from "react";
import {
    AdjustmentOperation,
    AdjustmentType,
    DemographicPrice,
    DemographicPricingGroup,
    Experience,
    LineItemTemplate,
    LineItemTemplateCode,
    PricingTerm,
    PricingType,
} from "types";
import {
    calculatePriceWithAdjustment,
    getDemographicPriceForLineItemTemplate,
    getDemographicPricesForExperienceCatalog,
} from "../../../models/DemographicPrice";
import { isDefaultDemographic } from "../../../models/DemographicUnit";
import { getEquipmentPricesForExperienceCatalog } from "../../../models/EquipmentPrice";
import { getLineItemTemplateByCode } from "../../../models/LineItemTemplate";
import { getDefaultPricingTerm } from "../../../models/PricingGroup";
import { getSeller } from "../../../utils/getSeller";
import { Currency } from "../../Common/Currency";
import { InlineConfirmDeletePopover } from "../../Common/InlineConfirmDeletePopover";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { getDemographicPriceMap, getInitialValues, getValidationSchema } from "./EditTieredPricingFormHelper";
import { useLocalizationStore } from "@/stores/localizationStore";

interface EditTieredPricingModalProps {
    isOpen: boolean;
    tierIndex: number;
    experience: Experience;
    lineItemTemplates: LineItemTemplate[];
    pricingGroup: DemographicPricingGroup;
    onClose: () => void;
    onDelete: () => void;
    onSubmit: (tierIndex: number, pricingTerm: PricingTerm) => void;
}

export const EditTieredPricingModal = ({
    isOpen,
    tierIndex,
    experience,
    lineItemTemplates,
    pricingGroup,
    onClose,
    onDelete,
    onSubmit,
}: EditTieredPricingModalProps) => {
    return (
        <Modal isOpen={isOpen} size={tierIndex > 0 ? "huge" : "medium"} onClose={onClose} onSubmit={onsubmit}>
            <ModalContent
                isOpen={isOpen}
                lineItemTemplates={lineItemTemplates}
                experience={experience}
                tierIndex={tierIndex}
                pricingGroup={pricingGroup}
                onClose={onClose}
                onDelete={onDelete}
                onSubmit={onSubmit}
            />
        </Modal>
    );
};

const ModalContent = ({
    isOpen,
    experience,
    pricingGroup,
    tierIndex,
    lineItemTemplates,
    onClose,
    onDelete,
    onSubmit,
}: EditTieredPricingModalProps) => {
    const isBasePriceTier = tierIndex === 0;
    const pricingTerm = pricingGroup.terms.length > tierIndex ? pricingGroup.terms[tierIndex] : getDefaultPricingTerm();
    const isPerOutingPricingGroup = pricingGroup.pricingType === PricingType.PER_OUTING;
    const fixedPriceLineItemTemplate = getLineItemTemplateByCode(
        lineItemTemplates,
        LineItemTemplateCode.PER_OUTING_PRICE,
    );
    const isEquipment = isEquipmentBased(experience);
    const demographicPrices = isPerOutingPricingGroup
        ? [getDemographicPriceForLineItemTemplate(pricingTerm, fixedPriceLineItemTemplate!)]
        : isEquipment
        ? getEquipmentPricesForExperienceCatalog(experience, pricingTerm)
        : getDemographicPricesForExperienceCatalog(experience, pricingTerm);

    const sortedDemographicPrices = sortBy(demographicPrices, "catalogItem.sequence");
    const initialValues = getInitialValues(sortedDemographicPrices, pricingTerm, tierIndex);
    const validationSchema = getValidationSchema(pricingGroup, tierIndex);

    const baseDemographicPriceMap: { [key: ID]: number } = getDemographicPriceMap(
        pricingGroup.terms[0]?.demographicPrices ?? [],
    );

    const showTable = (pricingGroup.pricingType === PricingType.PER_PERSON && isBasePriceTier) || !isBasePriceTier;

    return (
        <XolaFormik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => onSubmit(tierIndex, values)}
        >
            {({ handleSubmit, values, setFieldValue, errors }) => (
                <>
                    <Modal.Header>
                        Tier <NoTranslate>{tierIndex + 1}</NoTranslate>
                    </Modal.Header>

                    <Modal.Body>
                        <div className="space-y-4">
                            {isBasePriceTier && pricingGroup.pricingType === PricingType.PER_PERSON ? (
                                <Alert className="mb-10" color="primary">
                                    {isEquipment
                                        ? "These are Base Prices for each equipment"
                                        : "These are Base Prices for each demographic"}
                                </Alert>
                            ) : null}

                            {tierIndex > 0 ? (
                                <FormField name="quantityMin" label="For group size greater than or equal to ">
                                    <InputField isRequired type="number" />
                                </FormField>
                            ) : null}

                            <div className="space-y-2">
                                {pricingGroup.pricingType === PricingType.PER_OUTING && isBasePriceTier ? (
                                    <FormField label="Base Price" name={`demographicPrices.${0}.price`}>
                                        <InputField
                                            isRequired
                                            min="1"
                                            step="1"
                                            type="number"
                                            className="align-middle"
                                        />
                                    </FormField>
                                ) : (
                                    <Label>Terms</Label>
                                )}
                                {showTable ? (
                                    <Table>
                                        <Table.Head>
                                            <Table.Row>
                                                {isPerOutingPricingGroup ? null : (
                                                    <Table.Header className="!text-sm">
                                                        {isEquipment ? "Equipment" : "Demographic"}
                                                    </Table.Header>
                                                )}
                                                {isBasePriceTier ? (
                                                    <Table.Header className="!text-sm">Price</Table.Header>
                                                ) : (
                                                    <>
                                                        <Table.Header className="!text-sm">Base Price</Table.Header>
                                                        <Table.Header className="text-center !text-sm">
                                                            Adjustment
                                                        </Table.Header>
                                                        <Table.Header className="!text-sm">Final Price</Table.Header>
                                                    </>
                                                )}
                                            </Table.Row>
                                        </Table.Head>

                                        {pricingGroup.pricingType === PricingType.PER_OUTING ? (
                                            <Table.Body isStriped={false} className="divide-y divide-gray-lighter">
                                                <DemographicPriceItem
                                                    isBasePriceTier={isBasePriceTier}
                                                    showDemographicName={false}
                                                    basePrice={baseDemographicPriceMap[fixedPriceLineItemTemplate!.id]}
                                                    demographicPrice={values.demographicPrices[0]}
                                                    index={0}
                                                    error={errors?.demographicPrices?.[0]}
                                                    setFieldValue={setFieldValue}
                                                />
                                            </Table.Body>
                                        ) : (
                                            <Table.Body isStriped={false} className="divide-y divide-gray-lighter">
                                                {values.demographicPrices.map((demographicPrice, index) => (
                                                    <DemographicPriceItem
                                                        key={demographicPrice.template.id}
                                                        showDemographicName
                                                        isBasePriceTier={isBasePriceTier}
                                                        basePrice={
                                                            baseDemographicPriceMap[demographicPrice.template.id]
                                                        }
                                                        demographicPrice={demographicPrice}
                                                        index={index}
                                                        error={errors?.demographicPrices?.[index]}
                                                        setFieldValue={setFieldValue}
                                                    />
                                                ))}
                                            </Table.Body>
                                        )}
                                    </Table>
                                ) : null}
                            </div>
                        </div>
                    </Modal.Body>

                    <Modal.Footer className="flex items-center justify-between">
                        <InlineConfirmDeletePopover onConfirm={onDelete}>
                            {tierIndex > 0 && tierIndex < pricingGroup.terms.length ? (
                                <SecondaryButton>Delete</SecondaryButton>
                            ) : null}
                        </InlineConfirmDeletePopover>
                        <div className="space-x-2">
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                            <SubmitButton type="submit" disabled={!isOpen} onClick={handleSubmit}>
                                Save
                            </SubmitButton>
                        </div>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

interface DemographicPriceItemProps {
    isBasePriceTier: boolean;
    showDemographicName: boolean;
    basePrice: number;
    demographicPrice: DemographicPrice;
    index: number;
    error?: any;
    setFieldValue: (key: string, value: any) => void;
}

const DemographicPriceItem = ({
    isBasePriceTier,
    showDemographicName,
    basePrice,
    demographicPrice,
    index,
    error,
    setFieldValue,
}: DemographicPriceItemProps) => {
    const seller = getSeller();
    const locale = useLocalizationStore().getLocale();

    const finalPrice = calculatePriceWithAdjustment(basePrice, demographicPrice);

    const handleAdjustmentOperationChange = (index: number, value: AdjustmentOperation) => {
        if (value === AdjustmentOperation.FINAL) {
            setFieldValue(`demographicPrices.${index}.adjustmentType`, AdjustmentType.ABSOLUTE);
        }

        setFieldValue(`demographicPrices.${index}.adjustmentOperation`, value);
    };

    return (
        <Table.Row key={demographicPrice.template.id} className="justify-between">
            {showDemographicName ? (
                <Table.Cell className={isBasePriceTier ? "w-3/5" : "w-1/5"}>
                    <NoTranslate
                        if={!isDefaultDemographic(demographicPrice.catalogItem!.name)}
                        className="whitespace-normal break-all"
                    >
                        <DemographicIcon className="mr-1" name={demographicPrice.catalogItem!.name} />
                        {demographicPrice.catalogItem!.name}
                    </NoTranslate>
                </Table.Cell>
            ) : null}

            {isBasePriceTier ? (
                <Table.Cell className="w-2/5">
                    <FormField label={null} name={`demographicPrices.${index}.price`}>
                        <InputField isRequired min="1" step="1" type="number" className="align-middle" />
                    </FormField>
                </Table.Cell>
            ) : (
                <>
                    <Table.Cell className={showDemographicName ? "w-1/6" : "w-1/4"}>
                        {isNil(basePrice) ? null : <Currency>{basePrice}</Currency>}
                    </Table.Cell>
                    <Table.Cell className="flex w-65">
                        <Input
                            isRequired
                            isError={error?.adjustmentAmount}
                            className="h-9 w-25 rounded-r-none"
                            value={demographicPrice.adjustmentAmount}
                            type="number"
                            title={error?.adjustmentAmount}
                            onChange={(e: any) =>
                                setFieldValue(
                                    `demographicPrices.${index}.adjustmentAmount`,
                                    e.target.value ? Number(e.target.value) : undefined,
                                )
                            }
                        />
                        <Select
                            disabled={demographicPrice.adjustmentOperation === AdjustmentOperation.FINAL}
                            className="w-20 rounded-none"
                            value={demographicPrice.adjustmentType}
                            onChange={(e: any) =>
                                setFieldValue(`demographicPrices.${index}.adjustmentType`, e.target.value)
                            }
                        >
                            <option className="no-translate" value={AdjustmentType.ABSOLUTE}>
                                {getSymbol(seller.currency, locale)}
                            </option>
                            <option className="no-translate" value={AdjustmentType.PERCENT}>
                                %
                            </option>
                        </Select>
                        <Select
                            className="w-28 rounded-l-none"
                            value={demographicPrice.adjustmentOperation}
                            onChange={(e: any) =>
                                handleAdjustmentOperationChange(index, e.target.value as AdjustmentOperation)
                            }
                        >
                            <option value={AdjustmentOperation.MARKUP}>Markup</option>
                            <option value={AdjustmentOperation.MARKDOWN}>Markdown</option>
                            <option value={AdjustmentOperation.FINAL}>Final Price</option>
                        </Select>
                    </Table.Cell>
                    <Table.Cell className={showDemographicName ? "w-1/6" : "w-1/4"}>
                        {isNil(finalPrice) ? null : <Currency>{finalPrice}</Currency>}
                    </Table.Cell>
                </>
            )}
        </Table.Row>
    );
};
